const { findIndex, without } = require('ramda');

// Ex: "ID_RECTO:CROPPED,EXPIRED PROOF_OF_ADDRESS_HOST:BLURRY"
const parseTags = (tagsString = '') => {
  // Each bloc should look like: "DOCUMENT_KEY:TAG1,TAG2"
  const tagsByDocumentBloc = tagsString.split(' ');

  if (!tagsByDocumentBloc.length) {
    return [];
  }

  // Output looks like:
  //
  // [
  //   { documentType: ID_RECTO, tags: ["CROPPED", "EXPIRED"]},
  //   { documentType: PROOF_OF_ADDRESS_HOST, tags: ["BLURRY"]}
  // ]
  return tagsByDocumentBloc.reduce((acc, tagBloc) => {
    const docTagsPair = tagBloc.split(':');

    if (docTagsPair.length !== 2) {
      return acc;
    }

    const [docType, tags] = docTagsPair;

    return [
      ...acc,
      {
        documentType: docType,
        tags: [...tags.split(',')],
      },
    ];
  }, []);
};

const stringifyTags = (parsedTags = []) =>
  parsedTags.reduce((acc, bloc, currentIndex) => {
    if (!bloc.documentType || !bloc.tags || !bloc.tags.length) {
      return '';
    }

    const blocString = [bloc.documentType, ':', bloc.tags.join(',')];

    if (currentIndex === parsedTags.length - 1) {
      return acc.concat(...blocString);
    }

    // Add space separator between blocs
    return acc.concat(...blocString, ' ');
  }, '');

// Merges the docType/tags pair with existing pairs, and returns a formatted
// string.
const addTag =
  ({ documentType, tag }) =>
  (tagsString) => {
    const parsedTags = parseTags(tagsString);
    const indexForType = findIndex(
      (bloc) => bloc.documentType === documentType,
    )(parsedTags);

    // Doc type exists in existing tag
    if (indexForType > -1) {
      return stringifyTags([
        ...parsedTags.slice(0, indexForType),
        {
          documentType,
          tags: [...parsedTags[indexForType].tags, tag],
        },
        ...parsedTags.slice(indexForType + 1),
      ]);
    }

    return stringifyTags([...parsedTags, { documentType, tags: [tag] }]);
  };

const removeTag =
  ({ documentType, tag }) =>
  (tagsString) => {
    const parsedTags = parseTags(tagsString);
    const indexForType = findIndex(
      (bloc) => bloc.tags.includes(tag) && bloc.documentType === documentType,
    )(parsedTags);

    // Doc type exists in existing tags
    if (indexForType > -1) {
      if (parsedTags[indexForType].tags.length <= 1) {
        // Remove category altogether
        return stringifyTags([
          ...parsedTags.slice(0, indexForType),
          ...parsedTags.slice(indexForType + 1),
        ]);
      }

      return stringifyTags([
        ...parsedTags.slice(0, indexForType),
        {
          documentType,
          tags: without(tag, parsedTags[indexForType].tags),
        },
        ...parsedTags.slice(indexForType + 1),
      ]);
    }

    return tagsString;
  };

const removeType =
  ({ documentType }) =>
  (tagsString) => {
    const parsedTags = parseTags(tagsString);
    const indexForType = findIndex(
      (bloc) => bloc.documentType === documentType,
    )(parsedTags);

    // Doc type exists in existing tags
    if (indexForType > -1) {
      return stringifyTags([
        ...parsedTags.slice(0, indexForType),
        ...parsedTags.slice(indexForType + 1),
      ]);
    }

    return tagsString;
  };

module.exports = {
  addTag,
  parseTags,
  removeTag,
  removeType,
  stringifyTags,
};
